import { Box, Dropdown, Flex, Image, Typography } from "@qws-org/ui-kit";
import type { FC } from "react";
import React, { useState } from "react";

import type { GetSettingsQuery as GetSettingsQuerySdk } from "~/core/sdk";
import { ArrowDown, Globe } from "~/icons";
import { useDirectus } from "~/shared/utils/directus/useDirectus";
import {
  getLocaleHref,
  type LocaleAlternate,
} from "~/shared/utils/locale-navigation";

export type LocaleSelectProps = {
  languages: GetSettingsQuerySdk["languages"];
  availableLanguages: Array<string>;
  language: string;
  hreflangAlternates?: Array<LocaleAlternate>;
  variant?: "static" | "positioned";
  offset?: number;
  isNonModal?: boolean;
};

function isLanguageCode(
  code: string | null | undefined,
  availableLanguages: Array<string>,
): code is LanguageCode {
  if (code) {
    return availableLanguages.includes(code);
  }
  return false;
}

export const LocaleSelect: FC<LocaleSelectProps> = ({
  languages,
  language,
  availableLanguages,
  hreflangAlternates,
  variant = "positioned",
  offset,
  isNonModal = true,
}) => {
  const { getDirectusImage } = useDirectus();
  const [isActive, setIsActive] = useState(false);
  const visibleLanguages = languages.filter(
    (item) => !item?.hide_in_language_switcher,
  );
  const selectedLang = languages?.find((item) => {
    if (item?.code === language) {
      return item;
    }

    return undefined;
  });
  const changeLanguage = (index: number): void => {
    const code = visibleLanguages[index]?.code;

    if (code === language || !isLanguageCode(code, availableLanguages)) {
      return;
    }

    location.href = getLocaleHref({
      code,
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      alternates: hreflangAlternates,
    });
  };

  return (
    <Dropdown.Trigger
      onOpenChange={(isOpen) => setIsActive(isOpen)}
      onAction={(key) => {
        const index = Number(key);

        if (
          Number.isInteger(index) &&
          index >= 0 &&
          index < visibleLanguages.length
        ) {
          changeLanguage(index);
        }
      }}
      strategy="click"
      isNonModal={isNonModal}
      placement="bottom right"
      offset={offset}
      variant={variant}
      triggerElement={
        <button
          className="block w-full cursor-pointer border-0 bg-transparent p-0 text-inherit"
          data-testid="header-locale-switch"
          aria-label={`Change language, current ${selectedLang?.code ?? language}`}
          type="button"
        >
          <Flex
            align="center"
            justify="center"
            p={{ py: 8 }}
            color="text-primary-default"
            hover={{ color: "text-primary-hovered" }}
            className={` hover:stroke-[var(--colors-text-primary-hovered)]  ${isActive && "stroke-[var(--colors-text-selection)]"}`}
          >
            <Globe />
            <Typography
              size="medium"
              weight="semibold"
              textTransform="uppercase"
              p={{ pr: 8, pl: 4 }}
            >
              {selectedLang ? selectedLang?.code : "Pick"}
            </Typography>
            <ArrowDown
              className={`${isActive && "transform rotate-[-90deg]"}`}
            />
          </Flex>
        </button>
      }
      dropdownWrapper={(wrapperProps) => (
        <Box
          p={2}
          border={{ radius: "8px" }}
          md={{
            width: 200,
            p: 8,
            bg: "background-secondary-transparent",
          }}
          backdropBlur={10}
        >
          {wrapperProps.children}
        </Box>
      )}
    >
      {visibleLanguages.map(
        (item, index) =>
          item && (
            <Dropdown.Item key={index} textValue={item?.code}>
              <Flex
                align="center"
                justify="center"
                gap={16}
                key={index}
                p={{ py: 12 }}
                className="cursor-pointer"
                md={{
                  justify: "flex-start",
                  p: { px: 16 },
                  hover: { bg: "surface-primary-subtle" },
                  border: { radius: "8px" },
                }}
                textTransform="uppercase"
                onClick={() => {
                  changeLanguage(index);
                }}
              >
                {item?.icon?.id && (
                  <Image
                    value={getDirectusImage(item.icon.id ?? "", {
                      format: "webp",
                      width: 24,
                    })}
                  />
                )}
                <Typography size="medium" textTransform="uppercase">
                  {item?.code}
                </Typography>
              </Flex>
            </Dropdown.Item>
          ),
      )}
    </Dropdown.Trigger>
  );
};
